/**
 * Admin API клиент
 */

const API_BASE = '/api/admin';

function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const tg = (window as any).Telegram?.WebApp;
  if (tg?.initDataUnsafe?.user?.id) {
    headers['X-Telegram-User-Id'] = String(tg.initDataUnsafe.user.id);
  }
  if (tg?.initData) {
    headers['X-Telegram-Init-Data'] = tg.initData;
  }

  return headers;
}

// Типы
export interface AdminStats {
  totalCourses: number;
  publishedCourses: number;
  draftCourses: number;
  totalLessons: number;
  totalModules: number;
  totalQuestions: number;
  totalStudents: number;
  avgRating: number;
  completionRate: number;
}

export interface CourseListItem {
  id: string;
  name: string;
  description?: string;
  icon: string;
  category: string;
  difficulty: string;
  isPublished: boolean;
  lessonsCount: number;
  modulesCount: number;
}

export interface Course {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: string;
  difficulty: string;
  contentType: string;
  isPublished: boolean;
  isPremium: boolean;
  sections: Section[];
  stats: any;
  meta: any;
}

export interface Section {
  id: string;
  name: string;
  modules: Module[];
}

export interface Module {
  id: string;
  name: string;
  description?: string;
  lesson?: {
    id: string;
    type: string;
    duration?: number;
  };
  quizIds: string[];
  requiredScore: number;
}

export interface Lesson {
  id: string;
  collectionId: string;
  moduleId: string;
  title: string;
  type: string;
  content: {
    text: string;
    format: string;
    keyPoints: string[];
    examples: any[];
  };
  settings: any;
  meta: any;
}

// API функции

export async function checkAdminAccess(): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE}/check`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) return false;
    const data = await response.json();
    return data.isAdmin === true;
  } catch {
    return false;
  }
}

export async function getAdminStats(): Promise<{ stats: AdminStats; recentCourses: any[] }> {
  const response = await fetch(`${API_BASE}/stats`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('Failed to get stats');
  return response.json();
}

export async function getCourses(): Promise<CourseListItem[]> {
  const response = await fetch(`${API_BASE}/courses`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('Failed to get courses');
  const data = await response.json();
  return data.courses;
}

export async function getCourse(id: string): Promise<Course> {
  const response = await fetch(`${API_BASE}/courses/${id}`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('Failed to get course');
  const data = await response.json();
  return data.course;
}

export async function createCourse(course: Partial<Course>): Promise<Course> {
  const response = await fetch(`${API_BASE}/courses`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(course),
  });
  if (!response.ok) throw new Error('Failed to create course');
  const data = await response.json();
  return data.course;
}

export async function updateCourse(id: string, course: Partial<Course>): Promise<Course> {
  const response = await fetch(`${API_BASE}/courses/${id}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(course),
  });
  if (!response.ok) throw new Error('Failed to update course');
  const data = await response.json();
  return data.course;
}

export async function deleteCourse(id: string): Promise<void> {
  const response = await fetch(`${API_BASE}/courses/${id}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('Failed to delete course');
}

export async function publishCourse(id: string): Promise<void> {
  const response = await fetch(`${API_BASE}/courses/${id}/publish`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('Failed to publish course');
}

export async function unpublishCourse(id: string): Promise<void> {
  const response = await fetch(`${API_BASE}/courses/${id}/unpublish`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('Failed to unpublish course');
}

export async function getLessons(): Promise<Lesson[]> {
  const response = await fetch(`${API_BASE}/lessons`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('Failed to get lessons');
  const data = await response.json();
  return data.lessons;
}

export async function getLesson(id: string): Promise<Lesson> {
  const response = await fetch(`${API_BASE}/lessons/${id}?_t=${Date.now()}`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('Failed to get lesson');
  const data = await response.json();
  return data.lesson;
}

export async function createLesson(lesson: Partial<Lesson>): Promise<Lesson> {
  const response = await fetch(`${API_BASE}/lessons`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(lesson),
  });
  if (!response.ok) throw new Error('Failed to create lesson');
  const data = await response.json();
  return data.lesson;
}

export async function updateLesson(id: string, lesson: Partial<Lesson>): Promise<Lesson> {
  const response = await fetch(`${API_BASE}/lessons/${id}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(lesson),
  });
  if (!response.ok) throw new Error('Failed to update lesson');
  const data = await response.json();
  return data.lesson;
}

export async function deleteLesson(id: string): Promise<void> {
  const response = await fetch(`${API_BASE}/lessons/${id}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('Failed to delete lesson');
}

// Викторины
export interface QuizListItem {
  id: string;
  topic: string;
  category: string;
  categoryEmoji: string;
  questionsCount: number;
  difficulty: string;
}

export async function getQuizzes(): Promise<QuizListItem[]> {
  const response = await fetch(`${API_BASE}/quizzes`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('Failed to get quizzes');
  const data = await response.json();
  return data.quizzes;
}

// Quiz API
export interface QuizQuestion {
  // формат v2 (07–08.09.2026)
  type?: 'single' | 'hotspot' | 'labels' | 'order' | 'line' | 'categories' | 'number' | 'free';
  answer?: number;
  tolerance?: number;
  tolerancePercent?: number;
  unit?: string;
  accepted?: string[];
  keywords?: string[];
  maxLength?: number;

  categories?: { id: string; name: string }[];
  items?: { id: string; text: string; category: string }[];

  lines?: Array<{ id: string; points: number[]; correct: boolean; label?: string; explanation?: string }>;
  steps?: { id: string; text: string }[];
  distractors?: string[];
  skill?: string;
  critical?: boolean;
  anchor?: string;
  image?: { url: string; alt?: string };
  mode?: 'one' | 'all';
  zones?: Array<{ id: string; shape: 'rect' | 'poly'; coords: number[]; correct?: boolean; label?: string; explanation?: string }>;
  id: string;
  text: string;
  options: string[];
  correctIndex: number;
  explanation?: string;
  difficulty?: string;
}

export interface Quiz {
  id: string;
  topic: string;
  category: string;
  tags?: string[];
  questions: QuizQuestion[];
}

export async function getQuiz(id: string): Promise<Quiz> {
  const response = await fetch(`${API_BASE}/quizzes/${id}`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('Failed to get quiz');
  const data = await response.json();
  return data.quiz;
}

export async function updateQuiz(id: string, quiz: Partial<Quiz>): Promise<Quiz> {
  const response = await fetch(`${API_BASE}/quizzes/${id}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(quiz),
  });
  if (!response.ok) throw new Error('Failed to update quiz');
  const data = await response.json();
  return data.quiz;
}

/** Загрузка файла урока из админки: картинка, видео-файл или постер к нему. Возвращает адрес для вставки в текст. */
export async function uploadFile(
  file: Blob,
  opts: { courseId: string; kind: 'image' | 'video' | 'poster'; name?: string; filename?: string },
): Promise<{ url: string; absoluteUrl: string }> {
  const fd = new FormData();
  fd.append('courseId', opts.courseId);
  fd.append('kind', opts.kind);
  if (opts.name) fd.append('name', opts.name);
  fd.append('file', file, opts.filename || (file as File).name || 'file');
  const headers = getAuthHeaders();
  delete headers['Content-Type']; // границу multipart ставит браузер
  const response = await fetch(`${API_BASE}/upload`, { method: 'POST', headers, body: fd, credentials: 'include' });
  if (!response.ok) {
    let why = `${response.status}`;
    try { why = (await response.json()).error || why; } catch { /* пусто */ }
    throw new Error(why);
  }
  return response.json();
}
